import { useContext, useEffect, useRef } from 'react';
import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from '@/components/Toast';
import { showBrowserNotification, WebSocketContext } from '@/contexts/websocket';
import type { SocketMessagePayload } from '@/containers/Conversaciones/types';
import { playChime, unlockChime } from '@/utils/messageChime';

const TITLE_PREFIX = /^\(\d+\) /;

/** Lo que cambia al volver a la pestaña: lista, conversación abierta y contador. */
const refreshConversations = (qc: ReturnType<typeof useQueryClient>) =>
  qc.invalidateQueries({
    predicate: (q) =>
      q.queryKey[0] === 'conversations' &&
      (q.queryKey[1] === 'list' || q.queryKey[1] === 'detail' || q.queryKey[1] === 'summary')
  });

const nameOf = (c: SocketMessagePayload['conversation']) =>
  c.name || c.profile_name || c.phone || c.email || 'Un cliente';

const previewOf = (m: SocketMessagePayload['message']) => {
  if (m.type === 'image') return m.body || 'Mandó una foto';
  if (m.type === 'document') return m.label || 'Mandó un archivo';
  return m.body || m.label || 'Te escribió';
};

/**
 * Avisos de Conversaciones fuera de la conversación abierta (se monta en la carcasa del panel):
 * toast con «Ver», notificación del navegador con la pestaña oculta, «(n)» en el título con las
 * que esperan respuesta, la campana al día y un sonido corto (se apaga desde la bandeja). También
 * refresca la bandeja al volver a la pestaña.
 */
export const useConversationAlerts = (enabled: boolean) => {
  const subscribe = useContext(WebSocketContext)?.subscribe;
  const syncApiNotifications = useContext(WebSocketContext)?.syncApiNotifications;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const where = useRef({ pathname, search });
  where.current = { pathname, search };

  // El contador lo pide `useNavbarItems`; acá sólo se lee la misma clave.
  const { data: summary } = useQuery<{ unanswered?: number }>({
    queryKey: ['conversations', 'summary'],
    queryFn: skipToken,
    enabled
  });
  const unanswered = enabled ? (summary?.unanswered ?? 0) : 0;

  useEffect(() => {
    if (!enabled || !subscribe) return undefined;
    let bell: ReturnType<typeof setTimeout> | undefined;
    const off = subscribe<SocketMessagePayload>('conversation:message', ({ conversation, message }) => {
      if (!conversation || !message || message.kind !== 'in') return;
      // Un estado nuevo de un mensaje viejo (leído, reacción) también llega por acá: sólo los recientes.
      if (Date.now() - new Date(message.created_at).getTime() > 2 * 60 * 1000) return;
      const { pathname: path, search: qs } = where.current;
      const open = Number(new URLSearchParams(qs).get('c')) || null;
      const inInbox = path === '/conversaciones' || path === '/conversaciones/';
      const hidden = document.visibilityState === 'hidden';
      const go = () => navigate(`/conversaciones?c=${conversation.id}`);
      const name = nameOf(conversation);
      const text = previewOf(message);

      clearTimeout(bell);
      bell = setTimeout(() => void syncApiNotifications?.(), 1500);

      // Suena salvo que la estés mirando: esa conversación abierta, con la pestaña al frente.
      const watching = inInbox && open === conversation.id && !hidden && document.hasFocus();
      if (!watching) playChime({ key: message.id });

      if (hidden) {
        showBrowserNotification(`Mensaje de ${name}`, text, go);
        return;
      }
      // Con la bandeja a la vista, la fila ya lo muestra; con esa conversación abierta, el hilo.
      if (inInbox && (open === conversation.id || open === null)) return;
      toast.info(`Mensaje de ${name}`, {
        description: text.length > 90 ? `${text.slice(0, 90)}…` : text,
        duration: 8000,
        action: { label: 'Ver', onClick: go }
      });
    });
    return () => {
      clearTimeout(bell);
      off();
    };
  }, [enabled, subscribe, syncApiNotifications, navigate]);

  // El navegador sólo deja sonar después de que la persona tocó la página.
  useEffect(() => {
    if (!enabled) return undefined;
    const events = ['pointerdown', 'keydown'] as const;
    events.forEach((e) => window.addEventListener(e, unlockChime, { capture: true, passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, unlockChime, { capture: true }));
  }, [enabled]);

  // Al volver a la pestaña (después de estar oculta) se pide de nuevo lo que pudo cambiar.
  useEffect(() => {
    if (!enabled) return undefined;
    let wasHidden = document.visibilityState === 'hidden';
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        wasHidden = true;
        return;
      }
      if (!wasHidden) return;
      wasHidden = false;
      void refreshConversations(qc);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [enabled, qc]);

  // «(3) Agenda | Ágora»: las pantallas cambian el título, así que se vuelve a poner cuando lo pisan.
  useEffect(() => {
    const apply = () => {
      const base = document.title.replace(TITLE_PREFIX, '');
      const next = unanswered > 0 ? `(${unanswered}) ${base}` : base;
      if (document.title !== next) document.title = next;
    };
    apply();
    const el = document.querySelector('title');
    if (!el || typeof MutationObserver === 'undefined') return undefined;
    const obs = new MutationObserver(apply);
    obs.observe(el, { childList: true, characterData: true, subtree: true });
    return () => {
      obs.disconnect();
      document.title = document.title.replace(TITLE_PREFIX, '');
    };
  }, [unanswered, pathname]);
};
